import React, { useState, useEffect } from 'react'
import {
  Container,
  Table,
  Breadcrumb,
  InputGroup,
  FormControl,
  Card,
  Row,
  Col,
} from 'react-bootstrap'
import { Link, useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import useGetCliUsers from 'hooks/use-get-cli-users'
import useDebounce from 'hooks/use-debounce'
import { CliUser } from 'interfaces/cli-user'

const formatDate = (iso: string): string => {
  return new Date(iso).toLocaleDateString()
}

const CliUsersPage = (): React.ReactElement => {
  const history = useHistory()
  const location = useLocation()
  const queryParams = queryString.parse(location.search)

  const [page, setPage] = useState<number>(Number(queryParams.page) || 1)
  const [searchInput, setSearchInput] = useState<string>(
    (queryParams.query as string) || ''
  )

  const debouncedSearch = useDebounce(searchInput, 400)

  useEffect(() => {
    const newParams: Record<string, string> = {}
    if (page > 1) newParams.page = String(page)
    if (debouncedSearch) newParams.query = debouncedSearch

    const newSearch = queryString.stringify(newParams)
    if (newSearch !== location.search.slice(1)) {
      history.replace({ search: newSearch })
    }
  }, [page, debouncedSearch, history, location.search])

  useEffect(() => {
    // Reset to page 1 when search changes
    setPage(1)
  }, [debouncedSearch])

  const { data, isFetching } = useGetCliUsers({
    page,
    searchQuery: debouncedSearch,
  })

  const cliUsers: CliUser[] = data?.cli_users || []
  const pagination = data?.pagination

  return (
    <Container className="py-4">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
          Home
        </Breadcrumb.Item>
        <Breadcrumb.Item active>CLI Users</Breadcrumb.Item>
      </Breadcrumb>

      <h2 className="mb-4">CLI Users</h2>

      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>Search</InputGroup.Text>
                <FormControl
                  placeholder="Email or name"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col
              md={6}
              className="text-end text-muted small d-flex align-items-center justify-content-end"
            >
              {pagination && (
                <span>
                  {pagination.total_count} total · Page{' '}
                  {pagination.current_page} of {pagination.total_pages}
                </span>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Organization</th>
                <th>HubSpot</th>
                <th>Signed Up</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && cliUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    Loading...
                  </td>
                </tr>
              ) : cliUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    No CLI users found
                  </td>
                </tr>
              ) : (
                cliUsers.map((cliUser) => (
                  <tr key={cliUser.partner_integration_id}>
                    <td>
                      {cliUser.user_id ? (
                        <Link to={`/users/${cliUser.user_id}`}>
                          {cliUser.email || '—'}
                        </Link>
                      ) : (
                        cliUser.email || '—'
                      )}
                    </td>
                    <td>{cliUser.name || '—'}</td>
                    <td>
                      {cliUser.organization_id ? (
                        <Link to={`/organizations/${cliUser.organization_id}`}>
                          {cliUser.organization_name || '—'}
                        </Link>
                      ) : (
                        cliUser.organization_name || '—'
                      )}
                    </td>
                    <td>
                      {cliUser.hubspot_deal_url ? (
                        <a
                          href={cliUser.hubspot_deal_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Deal
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{formatDate(cliUser.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {pagination && pagination.total_pages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Previous
          </button>
          <span className="text-muted small">
            Page {pagination.current_page} of {pagination.total_pages}
          </span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={page >= pagination.total_pages || isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </Container>
  )
}

export default CliUsersPage
